import React, { useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { useI18n } from '../utilities/i18n';

// Выпадающий список с поиском (перенесён из админки ingame): начинаешь печатать — список сужается.
// Нужен там, где вариантов сотни (судьи, стадионы) и обычный <select> приходится долго листать.
const MAX_SHOWN = 60;
const LIST_W = 260;
const LIST_MAX_H = 260;

const normalize = (value) => String(value || '').toLowerCase().replace(/ё/g, 'е').trim();

export default function SearchSelect({
  value, options, onChange, placeholder, emptyLabel, className, disabled,
}) {
  const { t } = useI18n();
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const [active, setActive] = useState(0);
  const [pos, setPos] = useState(null);
  const wrapRef = useRef(null);
  const inputRef = useRef(null);
  const listRef = useRef(null);

  const selected = options.find((option) => String(option.value) === String(value));

  const filtered = useMemo(() => {
    const q = normalize(query);
    const list = q ? options.filter((option) => normalize(option.label).includes(q)) : options;
    return list.slice(0, MAX_SHOWN);
  }, [options, query]);

  // Пункт «не выбрано» всегда первым, если он задан
  const items = emptyLabel ? [{ value: '', label: emptyLabel, empty: true }, ...filtered] : filtered;

  const close = () => {
    setOpen(false);
    setQuery('');
    setPos(null);
  };

  useEffect(() => {
    if (!open) return undefined;
    const onDown = (event) => {
      if (wrapRef.current && !wrapRef.current.contains(event.target)) close();
    };
    document.addEventListener('mousedown', onDown);
    return () => document.removeEventListener('mousedown', onDown);
  }, [open]);

  // Список рисуется position: fixed — таблица лежит в прокручиваемом блоке, и absolute обрезался бы
  // в нижних строках. Снизу не хватает места — открываем вверх, у правого края — выравниваем вправо.
  useLayoutEffect(() => {
    if (!open) return undefined;
    const place = () => {
      if (!inputRef.current) return;
      const rect = inputRef.current.getBoundingClientRect();
      const below = window.innerHeight - rect.bottom;
      const up = below < LIST_MAX_H + 12 && rect.top > below;
      const left = Math.min(rect.left, window.innerWidth - LIST_W - 8);
      setPos({
        left: Math.max(8, left),
        top: up ? undefined : rect.bottom + 4,
        bottom: up ? window.innerHeight - rect.top + 4 : undefined,
      });
    };
    place();
    window.addEventListener('scroll', place, true);
    window.addEventListener('resize', place);
    return () => {
      window.removeEventListener('scroll', place, true);
      window.removeEventListener('resize', place);
    };
  }, [open]);

  useEffect(() => { setActive(emptyLabel && query ? 1 : 0); }, [query, emptyLabel]);

  useEffect(() => {
    if (!open || !listRef.current) return;
    const element = listRef.current.children[active];
    if (element) element.scrollIntoView({ block: 'nearest' });
  }, [active, open]);

  const pick = (item) => {
    onChange(item.value === '' ? '' : String(item.value));
    close();
  };

  const onKeyDown = (event) => {
    if (event.key === 'ArrowDown') {
      event.preventDefault();
      setOpen(true);
      setActive((index) => Math.min(index + 1, items.length - 1));
    } else if (event.key === 'ArrowUp') {
      event.preventDefault();
      setActive((index) => Math.max(index - 1, 0));
    } else if (event.key === 'Enter') {
      event.preventDefault();
      if (open && items[active]) pick(items[active]);
    } else if (event.key === 'Escape' || event.key === 'Tab') {
      close();
    }
  };

  const nothingFound = items.length === 0 || (emptyLabel && items.length === 1 && query);

  return (
    <div className={`search-select ${className || ''}`.trim()} ref={wrapRef}>
      <input
        ref={inputRef}
        type="text"
        className="st-input search-select-input"
        disabled={disabled}
        value={open ? query : (selected ? selected.label : '')}
        placeholder={selected ? selected.label : (placeholder || emptyLabel || '')}
        onFocus={() => setOpen(true)}
        onClick={() => setOpen(true)}
        onChange={(event) => { setQuery(event.target.value); setOpen(true); }}
        onKeyDown={onKeyDown}
      />
      {open && pos ? (
        <ul
          className="search-select-list"
          ref={listRef}
          style={{ left: pos.left, top: pos.top, bottom: pos.bottom, width: LIST_W, maxHeight: LIST_MAX_H }}
        >
          {nothingFound ? (
            <>
              {emptyLabel ? (
                <li
                  className={`search-select-item is-empty ${active === 0 ? 'is-active' : ''}`}
                  onMouseDown={(event) => { event.preventDefault(); pick(items[0]); }}
                >
                  {emptyLabel}
                </li>
              ) : null}
              <li className="search-select-none">{t('ssNothingFound')}</li>
            </>
          ) : items.map((item, index) => (
            <li
              key={item.empty ? '__empty' : item.value}
              className={[
                'search-select-item',
                item.empty ? 'is-empty' : '',
                index === active ? 'is-active' : '',
                String(item.value) === String(value) && !item.empty ? 'is-selected' : '',
              ].filter(Boolean).join(' ')}
              onMouseEnter={() => setActive(index)}
              onMouseDown={(event) => { event.preventDefault(); pick(item); }}
            >
              {item.label}
            </li>
          ))}
          {!query && options.length > MAX_SHOWN ? (
            <li className="search-select-none">{t('ssShownFirst', { shown: MAX_SHOWN, total: options.length })}</li>
          ) : null}
        </ul>
      ) : null}
    </div>
  );
}
